import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Check, AlertTriangle, AlertCircle, Lightbulb, TrendingUp, TrendingDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getAccountAnalysis, type AccountAnalysis, type HistoricalMonth, type AnalysisStats, type ForecastStatus } from '../../api';
import { formatCurrency } from '../../utils/formatters';
import { formatPor, formatPercent } from './columnRegistry';
import { BarChart as KitBarChart } from '../../../../components/charts/bar-chart';
import { Bar as KitBar } from '../../../../components/charts/bar';
import { BarXAxis as KitBarXAxis } from '../../../../components/charts/bar-x-axis';
import { ChartTooltip as KitChartTooltip } from '../../../../components/charts/tooltip';
import { ReferenceLine as KitReferenceLine } from '../../../../components/charts/reference-line';
import { prefersReducedMotion } from '../../../../components/ReportsNative/AnimatedValue';
// @ts-ignore - JavaScript module without types
import { StackedAreaChart as KitStackedAreaChart } from '../../../../components/ReportsNative/charts/StackedAreaChart';
import '../../../../components/ReportsNative/ReportsNative.css';
import { useForecastChartPalette } from '../../utils/chartPalette';

type ViewMode = '$' | 'POR' | '% of Revenue';

// The vendored kit is JSX with destructured defaults; TS infers every
// default-less prop as required, so loosen the types here.
type AnyComponent = React.ComponentType<any>;
const BarChart = KitBarChart as unknown as AnyComponent;
const Bar = KitBar as unknown as AnyComponent;
const BarXAxis = KitBarXAxis as unknown as AnyComponent;
const ChartTooltip = KitChartTooltip as unknown as AnyComponent;
const ReferenceLine = KitReferenceLine as unknown as AnyComponent;
const StackedAreaChart = KitStackedAreaChart as unknown as AnyComponent;

interface AnalysisTabProps {
  chartOfAccountId: number;
  orgId: number;
  locationId: number;
  versionId?: number;
  forecastMonth: number;
  forecastYear: number;
  currentForecastValue?: number; // Pass in the current forecast value from the grid
  accountType?: 'Revenue' | 'Expense';
  driverType?: string;
  initialSelectedDepartments?: string[];
  /** Forecasted occupied rooms per month from the version (YYYY-MM → rooms count) */
  occupiedRoomsPerMonth?: Record<string, number>;
  currency?: string | null;
  /**
   * Budget mode: every month of the plan from the active month through
   * December (in $). When set, the chart renders as one continuous line —
   * the trailing 12 months of actuals flowing into the budgeted months
   * ahead — instead of a single forecast bar.
   */
  forwardMonths?: HistoricalMonth[];
  forwardLabel?: string;
}

// ============ Skeleton Component ============

function AnalysisSkeleton() {
  return (
    <div className="sk-scope space-y-4 animate-pulse" aria-busy="true">
      {/* Insight Card Skeleton */}
      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="h-4 bg-slate-200 rounded w-3/4 mb-3" />
        <div className="h-3 bg-slate-200 rounded w-1/2 mb-4" />
        <div className="h-8 bg-slate-200 rounded w-48" />
      </div>

      {/* Chart Section Skeleton */}
      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="h-3 bg-slate-200 rounded w-40 mb-4" />
        {/* New look: the kit's own loading bars, like the chart that replaces it */}
        <div className="rn-sk-inset-only rn-chart-body mb-4" style={{ height: 96 }}>
          <BarChart className="rn-bklit" data={[]} status="loading" aspectRatio={null} style={{ height: '100%' }} margin={{ top: 4, right: 2, bottom: 4, left: 0 }} />
        </div>
        <div className="rn-sk-classic-only flex items-end gap-2 h-24 mb-4">
          {Array.from({ length: 13 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 bg-slate-200 rounded-t"
              style={{ height: `${30 + ((i * 37) % 60)}%` }}
            />
          ))}
        </div>
        <div className="flex gap-4">
          <div className="h-3 bg-slate-200 rounded w-16" />
          <div className="h-3 bg-slate-200 rounded w-16" />
          <div className="h-3 bg-slate-200 rounded w-20" />
        </div>
      </div>

      {/* Stats Grid Skeleton */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="h-3 bg-slate-200 rounded w-20 mb-2" />
          <div className="h-5 bg-slate-200 rounded w-16" />
        </div>
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="h-3 bg-slate-200 rounded w-20 mb-2" />
          <div className="h-5 bg-slate-200 rounded w-12" />
        </div>
      </div>
    </div>
  );
}

// ============ Mini Bar Chart Component ============
// bklit kit BarChart — same settings as the report column charts
// (GroupedColumnChart): 3px column caps, 4 gridline rows, peak-only y label,
// kit spring entrance. `chartKey` remounts the chart so the entrance replays
// when the account changes.

interface MiniChartProps {
  data: HistoricalMonth[];
  currentForecast: HistoricalMonth | null;
  stats: AnalysisStats;
  formatValue: (v: number) => string;
  chartKey?: string | number;
  /** Budget months ahead (active month → Dec). Switches to the line variant. */
  forwardMonths?: HistoricalMonth[] | null;
  forwardLabel?: string;
  currency?: string | null;
  yFormat?: string;
}

const ACTUAL_FILL = 'var(--chart-line-primary)';
const FORECAST_FILL = 'color-mix(in srgb, var(--chart-line-primary) 38%, var(--chart-background))';
const AVG_STROKE = 'var(--lodge-onyx-500, #55555f)';

function MiniChart({ data, currentForecast, stats, formatValue, chartKey, forwardMonths, forwardLabel = 'Budget', currency, yFormat }: MiniChartProps) {
  if (forwardMonths && forwardMonths.length > 0) {
    return (
      <ForwardLineChart
        data={data}
        forwardMonths={forwardMonths}
        forwardLabel={forwardLabel}
        stats={stats}
        formatValue={formatValue}
        chartKey={chartKey}
        currency={currency}
        yFormat={yFormat}
      />
    );
  }
  return (
    <ForecastBarChart
      data={data}
      currentForecast={currentForecast}
      stats={stats}
      formatValue={formatValue}
      chartKey={chartKey}
    />
  );
}

/** High / average / low over the trailing 12 months, on the absolute scale. */
function buildRefs(stats: AnalysisStats, tickFormat: (v: number) => string) {
  const high = Math.max(Math.abs(stats.min), Math.abs(stats.max));
  const low = Math.min(Math.abs(stats.min), Math.abs(stats.max));
  const average = Math.abs(stats.mean);
  return [
    { key: 'high', value: high, text: `high ${tickFormat(high)}` },
    { key: 'avg', value: average, text: `avg ${tickFormat(average)}` },
    { key: 'low', value: low, text: `low ${tickFormat(low)}` },
  ].filter(r => Number.isFinite(r.value) && r.value > 0);
}

function makeTickFormat(formatValue: (v: number) => string) {
  return (v: number) => {
    const abs = Math.abs(v);
    const text = formatValue(v);
    if (abs >= 1000 && /^-?[$€£¥]?[\d,]+(\.\d+)?$/.test(text)) {
      const prefix = text.match(/^-?[$€£¥]?/)?.[0] ?? '';
      const compact = abs >= 1_000_000 ? `${(abs / 1_000_000).toFixed(1)}M` : `${Math.round(abs / 1000)}K`;
      return `${prefix}${compact}`;
    }
    return text;
  };
}

// ---- Line variant (budget mode): trailing actuals → months ahead ----------
// Same past/future treatment as the Revenue Forecast report's "Actual +
// On-the-Books" chart: ONE continuous line whose color flips (and dashes)
// at the first budgeted month, with a labeled divider.

// Same future color the Revenue Forecast report uses for OTB.
const FUTURE_STROKE = 'var(--lodge-onyx-600)';

interface ForwardLineChartProps {
  data: HistoricalMonth[];
  forwardMonths: HistoricalMonth[];
  forwardLabel: string;
  stats: AnalysisStats;
  formatValue: (v: number) => string;
  chartKey?: string | number;
  currency?: string | null;
  yFormat?: string;
}

function ForwardLineChart({ data, forwardMonths, forwardLabel, stats, formatValue, chartKey, currency, yFormat = 'currencyCompact' }: ForwardLineChartProps) {
  const chartPalette = useForecastChartPalette();
  const futureStroke = chartPalette.inset ? chartPalette.colors.secondary : FUTURE_STROKE;
  const rows = useMemo(() => {
    const past = data.map(d => ({ month: d.month, label: d.displayMonth, value: Math.abs(d.value), isForecast: false }));
    const future = forwardMonths.map(d => ({ month: d.month, label: d.displayMonth, value: Math.abs(d.value), isForecast: true }));
    return [...past, ...future];
  }, [data, forwardMonths]);
  const splitX = forwardMonths[0]?.month;
  const tickFormat = makeTickFormat(formatValue);
  const refs = buildRefs(stats, tickFormat).map(r => ({
    key: r.key,
    value: r.value,
    label: r.text,
    color: r.key === 'avg' ? AVG_STROKE : 'var(--lodge-onyx-300, #b8b8c4)',
    labelColor: r.key === 'avg' ? AVG_STROKE : 'var(--lodge-onyx-400, #8a8a96)',
    opacity: r.key === 'avg' ? 0.9 : 0.8,
  }));
  const firstYear = forwardMonths[0]?.year;
  const lastLabel = forwardMonths[forwardMonths.length - 1]?.displayMonth;

  return (
    <div className="p-4 bg-white rounded-lg border border-slate-200">
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
        Last 12 Months + {forwardLabel}
      </div>
      {firstYear && lastLabel && (
        <div className="text-[11px] text-slate-400 mt-0.5">
          {forwardLabel} {forwardMonths[0].displayMonth}–{lastLabel} {firstYear}
        </div>
      )}

      <div key={chartKey} style={{ marginTop: 4 }}>
        <StackedAreaChart
          data={rows}
          xKey="month"
          xLabelKey="label"
          split={{
            key: 'value',
            x: splitX,
            past: { label: 'Actual', color: ACTUAL_FILL },
            future: { label: forwardLabel, color: futureStroke },
            name: 'Value',
          }}
          referenceX={splitX}
          referenceLabel={forwardLabel}
          yReferences={refs}
          yFormat={yFormat}
          currency={currency || undefined}
          legend={false}
          dots
          height={190}
          margin={{ top: 16, right: 64, bottom: 0, left: 0 }}
        />
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 pt-3 border-t border-slate-100">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0" style={{ borderTop: `2px solid ${ACTUAL_FILL}` }} />
          <span className="text-[11px] text-slate-500">Actual</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0" style={{ borderTop: `2px dashed ${futureStroke}` }} />
          <span className="text-[11px] text-slate-500">{forwardLabel}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0" style={{ borderTop: `1px dashed ${AVG_STROKE}` }} />
          <span className="text-[11px] text-slate-500">12-mo high / avg / low</span>
        </div>
      </div>
    </div>
  );
}

// ---- Bar variant (forecast mode): trailing actuals + one forecast bar -----

function ForecastBarChart({ data, currentForecast, stats, formatValue, chartKey }: Omit<MiniChartProps, 'forwardMonths' | 'forwardLabel' | 'currency' | 'yFormat'>) {
  // Combine historical data with forecast for display
  // Use absolute values to handle revenue accounts (stored as negative but displayed positive)
  const chartData = useMemo(() => {
    const combined = data.map(d => ({ ...d, value: Math.abs(d.value) }));
    if (currentForecast) combined.push({ ...currentForecast, value: Math.abs(currentForecast.value) });
    return combined;
  }, [data, currentForecast]);
  // The band scale keys on `month` (unique, "2025-08"); the axis shows the
  // short month name via the shell's `xLabels`.
  const xLabels = useMemo(() => chartData.map(d => d.displayMonth), [chartData]);

  const tickFormat = makeTickFormat(formatValue);
  // High / average / low over the 12 months, on the same absolute scale as
  // the bars, each labeled in the right gutter.
  const refs = buildRefs(stats, tickFormat);

  return (
    <div className="p-4 bg-white rounded-lg border border-slate-200">
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
        Last 12 Months + Forecast
      </div>

      <div className="rn-chart-body" style={{ height: 190 }}>
        <BarChart
          key={chartKey}
          className="rn-bklit"
          data={chartData}
          xDataKey="month"
          xLabels={xLabels}
          margin={{ top: 16, right: 60, bottom: 30, left: 8 }}
          aspectRatio={null}
          style={{ height: '100%' }}
          barGap={0.35}
          animationDuration={prefersReducedMotion() ? 0 : undefined}
        >
          {/* No gridlines or y ticks: the labeled average line is the only
              scale reference the reader needs here; a hairline baseline
              grounds the bars. */}
          <ReferenceLine y={0} stroke="var(--lodge-onyx-200, #d9d9e0)" strokeDasharray="0" />
          <BarXAxis />
          {refs.map(r => (
            <ReferenceLine
              key={r.key}
              y={r.value}
              stroke={r.key === 'avg' ? AVG_STROKE : 'var(--lodge-onyx-300, #b8b8c4)'}
              strokeDasharray="3 4"
              opacity={r.key === 'avg' ? 0.9 : 0.8}
              label={r.text}
              labelFill={r.key === 'avg' ? AVG_STROKE : 'var(--lodge-onyx-400, #8a8a96)'}
              labelFontSize={10}
              labelDx={58}
              labelDy={8}
            />
          ))}
          <ChartTooltip
            title={null}
            showDots={false}
            rows={(point: { isForecast?: boolean; value: number }) => [{
              color: point.isForecast ? FORECAST_FILL : ACTUAL_FILL,
              label: point.isForecast ? 'Forecast' : 'Actual',
              value: formatValue(point.value),
            }]}
          />
          <Bar
            dataKey="value"
            lineCap={3}
            fill={(point: { isForecast?: boolean }) => (point.isForecast ? FORECAST_FILL : ACTUAL_FILL)}
          />
        </BarChart>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 pt-3 border-t border-slate-100">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm" style={{ background: ACTUAL_FILL }} />
          <span className="text-[11px] text-slate-500">Actual</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm" style={{ background: FORECAST_FILL }} />
          <span className="text-[11px] text-slate-500">Forecast</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0" style={{ borderTop: `1px dashed ${AVG_STROKE}` }} />
          <span className="text-[11px] text-slate-500">12-mo high / avg / low</span>
        </div>
      </div>
    </div>
  );
}

// ============ Insight Card Component ============

interface InsightCardProps {
  insight: AccountAnalysis['insight'];
  forecastStatus: AccountAnalysis['forecastStatus'];
}

function InsightCard({ insight, forecastStatus }: InsightCardProps) {
  const getStatusIcon = () => {
    switch (insight.statusType) {
      case 'success':
        return <Check size={16} className="text-green-600" />;
      case 'warning':
        return <AlertTriangle size={16} className="text-amber-600" />;
      case 'danger':
        return <AlertCircle size={16} className="text-red-600" />;
    }
  };

  const getStatusStyles = () => {
    switch (insight.statusType) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-700';
      case 'warning':
        return 'bg-amber-50 border-amber-200 text-amber-700';
      case 'danger':
        return 'bg-red-50 border-red-200 text-red-700';
    }
  };

  // Parse the insight text to highlight the range values ($X - $Y or X% - Y%)
  const highlightedText = insight.text.replace(
    /(\$[\d,.]+[KM]?\s*-\s*\$[\d,.]+[KM]?|[\d,.]+%\s*-\s*[\d,.]+%)/g,
    '<strong class="text-slate-800">$1</strong>'
  );

  return (
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
      <p
        className="text-sm text-slate-600 mb-3"
        dangerouslySetInnerHTML={{ __html: highlightedText }}
      />
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${getStatusStyles()}`}>
        {getStatusIcon()}
        <span className="text-sm font-medium">{insight.statusMessage}</span>
      </div>
    </div>
  );
}

// ============ Stats Grid Component ============

interface StatsGridProps {
  stats: AnalysisStats;
  formatValue: (v: number) => string;
}

function StatsGrid({ stats, formatValue }: StatsGridProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
          12-Month Avg
        </div>
        <div className="text-lg font-semibold text-slate-800">
          {formatValue(stats.mean)}
        </div>
      </div>
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
          Variance
        </div>
        <div className="text-lg font-semibold text-slate-800">
          ±{Math.round(stats.variance)}%
        </div>
      </div>
    </div>
  );
}

// ============ Context Note Component ============

interface ContextNoteProps {
  contextNote: string | null;
  statusType: 'success' | 'warning' | 'danger';
}

function ContextNote({ contextNote, statusType }: ContextNoteProps) {
  if (!contextNote) return null;

  const getBgColor = () => {
    switch (statusType) {
      case 'success':
        return 'bg-blue-50 border-blue-200';
      case 'warning':
        return 'bg-amber-50 border-amber-200';
      case 'danger':
        return 'bg-red-50 border-red-200';
    }
  };

  const getIcon = () => {
    switch (statusType) {
      case 'success':
        return <Lightbulb size={14} className="text-blue-500" />;
      case 'warning':
        return <TrendingUp size={14} className="text-amber-500" />;
      case 'danger':
        return <TrendingDown size={14} className="text-red-500" />;
    }
  };

  return (
    <div className={`p-3 rounded-lg border ${getBgColor()}`}>
      <div className="flex items-start gap-2">
        <div className="mt-0.5">{getIcon()}</div>
        <p className="text-sm text-slate-600">{contextNote}</p>
      </div>
    </div>
  );
}

// ============ Main Analysis Tab Component ============

export function AnalysisTab({
  chartOfAccountId,
  orgId,
  locationId,
  versionId,
  forecastMonth,
  forecastYear,
  currentForecastValue,
  accountType,
  initialSelectedDepartments,
  occupiedRoomsPerMonth,
  currency,
  forwardMonths,
  forwardLabel,
}: AnalysisTabProps) {
  const showToggle = accountType === 'Expense';
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('$');
  const [selectedDepts, setSelectedDepts] = useState<string[]>(
    initialSelectedDepartments ?? []
  );

  // Animation state for department chiclet panel
  const [showDepts, setShowDepts] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Fetch analysis data
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['accountAnalysis', orgId, locationId, chartOfAccountId, versionId, forecastMonth, forecastYear],
    queryFn: () => getAccountAnalysis({
      orgId,
      locationId,
      chartOfAccountId,
      versionId,
      forecastMonth,
      forecastYear,
    }),
    enabled: !!orgId && !!locationId && !!chartOfAccountId && !!forecastMonth && !!forecastYear,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Show skeleton for minimum duration for smooth transition
  useEffect(() => {
    if (!isLoading && data) {
      const timer = setTimeout(() => {
        setShowSkeleton(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isLoading, data]);

  // Override forecast value with the one from the grid and recalculate insights
  const analysisData = useMemo(() => {
    if (!data) return null;
    if (currentForecastValue === undefined || !data.currentForecast || !data.stats) {
      return data;
    }

    // Use the grid's forecast value
    const forecastValue = currentForecastValue;
    const { min, max } = data.stats;

    // Recalculate forecast status
    let forecastStatus: ForecastStatus = 'in_range';
    let forecastPosition = 50;
    let deviationPercent = 0;

    if (forecastValue !== 0) {
      if (max !== min) {
        forecastPosition = Math.max(0, Math.min(100, ((forecastValue - min) / (max - min)) * 100));
      }

      if (forecastValue >= min && forecastValue <= max) {
        forecastStatus = 'in_range';
      } else if (forecastValue > max) {
        deviationPercent = ((forecastValue - max) / max) * 100;
        forecastStatus = deviationPercent > 20 ? 'high_danger' : 'high_warning';
        forecastPosition = 100;
      } else {
        deviationPercent = ((min - forecastValue) / min) * 100;
        forecastStatus = deviationPercent > 20 ? 'low_danger' : 'low_warning';
        forecastPosition = 0;
      }
    }

    // Regenerate insight text with correct forecast value
    const forecastFormatted = formatCurrency(forecastValue, true, currency);
    const forecastMonthName = new Date(forecastYear, forecastMonth - 1).toLocaleString('en', { month: 'long' });

    let statusMessage = '';
    let statusType: 'success' | 'warning' | 'danger' = 'success';
    let contextNote = '';

    switch (forecastStatus) {
      case 'in_range':
        statusMessage = 'Current forecast is in line with this range';
        statusType = 'success';
        if (forecastPosition > 75) {
          contextNote = `${forecastMonthName} forecast (${forecastFormatted}) is at the high end of your typical range. This is above average but within historical bounds.`;
        } else if (forecastPosition < 25) {
          contextNote = `${forecastMonthName} forecast (${forecastFormatted}) is at the low end of your typical range. This is below average but within historical bounds.`;
        } else {
          contextNote = `${forecastMonthName} forecast (${forecastFormatted}) is in the middle of your typical range.`;
        }
        break;
      case 'high_warning':
        statusMessage = 'Current forecast is slightly above typical range';
        statusType = 'warning';
        contextNote = `${forecastMonthName} forecast (${forecastFormatted}) exceeds your typical high by ${Math.round(deviationPercent)}%. Consider reviewing this value.`;
        break;
      case 'high_danger':
        statusMessage = 'Current forecast is significantly above typical range';
        statusType = 'danger';
        contextNote = `${forecastMonthName} forecast (${forecastFormatted}) exceeds your typical high by ${Math.round(deviationPercent)}%. This is unusually high.`;
        break;
      case 'low_warning':
        statusMessage = 'Current forecast is slightly below typical range';
        statusType = 'warning';
        contextNote = `${forecastMonthName} forecast (${forecastFormatted}) is below your typical low by ${Math.round(deviationPercent)}%. Consider reviewing this value.`;
        break;
      case 'low_danger':
        statusMessage = 'Current forecast is significantly below typical range';
        statusType = 'danger';
        contextNote = `${forecastMonthName} forecast (${forecastFormatted}) is below your typical low by ${Math.round(deviationPercent)}%. This is unusually low.`;
        break;
    }

    return {
      ...data,
      currentForecast: {
        ...data.currentForecast,
        value: forecastValue,
      },
      forecastStatus,
      insight: {
        ...data.insight,
        statusMessage,
        statusType,
        contextNote,
      },
    };
  }, [data, currentForecastValue, forecastMonth, forecastYear, currency]);

  // Derive available revenue departments from API response (no extra fetch needed)
  const availableDepts = useMemo(() => {
    if (!analysisData?.revenueByDepartmentPerMonth) return [];
    const deptSet = new Set<string>();
    Object.values(analysisData.revenueByDepartmentPerMonth).forEach(monthData => {
      Object.keys(monthData).forEach(k => { if (k !== '_total') deptSet.add(k); });
    });
    return Array.from(deptSet).sort();
  }, [analysisData]);

  // Transform historical months based on view mode (same logic as POR/% Rev columns in grid)
  const transformedMonths = useMemo(() => {
    if (!analysisData) return [];
    return analysisData.historicalMonths.map(m => {
      if (viewMode === 'POR') {
        // Historical months use PMS actual occupied rooms
        const rooms = analysisData.roomStatsPerMonth?.[m.month] ?? 0;
        return { ...m, value: rooms > 0 ? m.value / rooms : 0 };
      }
      if (viewMode === '% of Revenue') {
        // Same as `pctRev` column: (value / totalRevenue) * 100
        // Filtered to selectedDepts when specified (matching "% of Selected Revenue" driver)
        const monthRevData = analysisData.revenueByDepartmentPerMonth?.[m.month] ?? {};
        const totalRev = selectedDepts.length > 0
          ? selectedDepts.reduce((sum, d) => sum + (monthRevData[d] ?? 0), 0)
          : (monthRevData._total ?? 0);
        return { ...m, value: totalRev > 0 ? (m.value / totalRev) * 100 : 0 };
      }
      return m; // '$' mode: unchanged
    });
  }, [analysisData, viewMode, selectedDepts]);

  // Transform currentForecast value the same way
  const transformedForecast = useMemo(() => {
    if (!analysisData?.currentForecast) return null;
    const fc = analysisData.currentForecast;
    if (viewMode === 'POR') {
      // occupiedRoomsPerMonth keys are short month names: jan, feb, mar, ...
      const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
      const monthKey = MONTH_KEYS[forecastMonth - 1];
      const rooms = occupiedRoomsPerMonth?.[monthKey] ?? 0;
      return { ...fc, value: rooms > 0 ? fc.value / rooms : 0 };
    }
    if (viewMode === '% of Revenue') {
      // For the forecast bar, use forecasted revenue by department
      const forecastRevData = analysisData.forecastRevenueByDepartment ?? {};
      const totalRev = selectedDepts.length > 0
        ? selectedDepts.reduce((sum, d) => sum + (forecastRevData[d] ?? 0), 0)
        : (forecastRevData._total ?? 0);
      return { ...fc, value: totalRev > 0 ? (fc.value / totalRev) * 100 : 0 };
    }
    return fc;
  }, [analysisData, viewMode, selectedDepts, forecastMonth, forecastYear]);

  // Budget mode: transform every month ahead the same way. POR divides by
  // that month's forecast occupied rooms; % of Revenue only has a revenue
  // denominator for the active month, so it collapses to that one point.
  const transformedForward = useMemo(() => {
    if (!forwardMonths || forwardMonths.length === 0) return null;
    if (viewMode === 'POR') {
      const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
      return forwardMonths.map(m => {
        const rooms = occupiedRoomsPerMonth?.[MONTH_KEYS[m.monthNum - 1]] ?? 0;
        return { ...m, value: rooms > 0 ? m.value / rooms : 0 };
      });
    }
    if (viewMode === '% of Revenue') {
      return transformedForecast ? [transformedForecast] : null;
    }
    return forwardMonths;
  }, [forwardMonths, viewMode, occupiedRoomsPerMonth, transformedForecast]);
  const chartYFormat = viewMode === 'POR' ? 'currency' : viewMode === '% of Revenue' ? 'percent1' : 'currencyCompact';

  // Recompute stats from transformed data when not in $ mode
  const transformedStats = useMemo(() => {
    if (!analysisData?.stats) return null;
    if (viewMode === '$') return analysisData.stats;
    if (transformedMonths.length < 3) return null;
    const values = transformedMonths.map(m => Math.abs(m.value));
    const min = Math.min(...values);
    const max = Math.max(...values);
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
    const stdDev = Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / values.length);
    const variance = mean !== 0 ? (stdDev / Math.abs(mean)) * 100 : 0;
    return { min, max, mean, stdDev, variance: Math.round(variance * 10) / 10, range: max - min };
  }, [analysisData, viewMode, transformedMonths]);

  // Compute insight text + forecast status for POR / % of Revenue modes
  const transformedInsight = useMemo(() => {
    if (viewMode === '$' || !transformedStats || !transformedForecast) return null;
    const fmt = viewMode === 'POR' ? formatPor : formatPercent;
    const { min, max } = transformedStats;
    const fcValue = Math.abs(transformedForecast.value);
    const minFmt = fmt(min);
    const maxFmt = fmt(max);
    const fcFmt = fmt(fcValue);
    const monthName = new Date(forecastYear, forecastMonth - 1).toLocaleString('en', { month: 'long' });
    const modeLabel = viewMode === 'POR' ? '$POR' : '% of revenue';

    const text = `This account typically books between ${minFmt} - ${maxFmt} ${modeLabel} per month based on the last 12 months.`;

    let forecastStatus: ForecastStatus = 'in_range';
    let forecastPosition = 50;
    let deviationPercent = 0;

    if (max !== min) {
      forecastPosition = Math.max(0, Math.min(100, ((fcValue - min) / (max - min)) * 100));
    }

    if (fcValue >= min && fcValue <= max) {
      forecastStatus = 'in_range';
    } else if (fcValue > max && max !== 0) {
      deviationPercent = ((fcValue - max) / max) * 100;
      forecastStatus = deviationPercent > 20 ? 'high_danger' : 'high_warning';
      forecastPosition = 100;
    } else if (fcValue < min && min !== 0) {
      deviationPercent = ((min - fcValue) / min) * 100;
      forecastStatus = deviationPercent > 20 ? 'low_danger' : 'low_warning';
      forecastPosition = 0;
    }

    let statusMessage = '';
    let statusType: 'success' | 'warning' | 'danger' = 'success';
    let contextNote = '';

    switch (forecastStatus) {
      case 'in_range':
        statusMessage = 'Current forecast is in line with this range';
        statusType = 'success';
        contextNote = forecastPosition > 75
          ? `${monthName} forecast (${fcFmt}) is at the high end of your typical range.`
          : forecastPosition < 25
          ? `${monthName} forecast (${fcFmt}) is at the low end of your typical range.`
          : `${monthName} forecast (${fcFmt}) is in the middle of your typical range.`;
        break;
      case 'high_warning':
        statusMessage = 'Current forecast is slightly above typical range';
        statusType = 'warning';
        contextNote = `${monthName} forecast (${fcFmt}) exceeds your typical high by ${Math.round(deviationPercent)}%. Consider reviewing this value.`;
        break;
      case 'high_danger':
        statusMessage = 'Current forecast is significantly above typical range';
        statusType = 'danger';
        contextNote = `${monthName} forecast (${fcFmt}) exceeds your typical high by ${Math.round(deviationPercent)}%. This is unusually high.`;
        break;
      case 'low_warning':
        statusMessage = 'Current forecast is slightly below typical range';
        statusType = 'warning';
        contextNote = `${monthName} forecast (${fcFmt}) is below your typical low by ${Math.round(deviationPercent)}%. Consider reviewing this value.`;
        break;
      case 'low_danger':
        statusMessage = 'Current forecast is significantly below typical range';
        statusType = 'danger';
        contextNote = `${monthName} forecast (${fcFmt}) is below your typical low by ${Math.round(deviationPercent)}%. This is unusually low.`;
        break;
    }

    return { insight: { text, statusMessage, statusType, contextNote }, forecastStatus };
  }, [viewMode, transformedStats, transformedForecast, forecastMonth, forecastYear]);

  // Format value based on view mode (reuses formatPor/formatPercent from columnRegistry)
  const formatValue = (v: number) => {
    if (viewMode === '% of Revenue') return formatPercent(v);
    if (viewMode === 'POR') return formatPor(v);
    return formatCurrency(v, true, currency);
  };

  // Sync selected departments when the account changes (panel stays mounted across account switches)
  useEffect(() => {
    setSelectedDepts(initialSelectedDepartments ?? []);
  }, [chartOfAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear all pending animation timeouts on unmount
  useEffect(() => {
    return () => { timeoutsRef.current.forEach(clearTimeout); };
  }, []);

  const handleModeChange = (mode: ViewMode) => {
    // Cancel any in-flight animation
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setViewMode(mode);
    if (mode === '% of Revenue') {
      // Phase 1: expand after toggle settles
      timeoutsRef.current.push(setTimeout(() => setShowDepts(true), 120));
      // Phase 2: reveal chiclets one at a time
      availableDepts.forEach((_, i) => {
        timeoutsRef.current.push(
          setTimeout(() => setVisibleCount(i + 1), 350 + i * 140)
        );
      });
    } else {
      // Collapse: chiclets vanish immediately, container closes after 100ms
      setVisibleCount(0);
      timeoutsRef.current.push(setTimeout(() => setShowDepts(false), 100));
    }
  };

  const toggleDept = (dept: string) => {
    setSelectedDepts(prev =>
      prev.includes(dept) ? prev.filter(d => d !== dept) : [...prev, dept]
    );
  };

  // Loading state
  if (showSkeleton || isLoading || !analysisData) {
    return <AnalysisSkeleton />;
  }

  // Error state
  if (isError) {
    return (
      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle size={16} />
          <span className="text-sm font-medium">Failed to load analysis</span>
        </div>
        <p className="text-xs text-red-600 mt-1">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  // Insufficient data state
  if (!analysisData.stats) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertTriangle size={16} />
            <span className="text-sm font-medium">{analysisData.insight.statusMessage}</span>
          </div>
          <p className="text-xs text-amber-600 mt-1">
            {analysisData.insight.text}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Insight Card */}
      {viewMode === '$' ? (
        <InsightCard
          insight={analysisData.insight}
          forecastStatus={analysisData.forecastStatus}
        />
      ) : transformedInsight ? (
        <InsightCard
          insight={transformedInsight.insight}
          forecastStatus={transformedInsight.forecastStatus}
        />
      ) : null}

      {/* View mode toggle + animated department chiclet panel (expense accounts only) */}
      {showToggle && <div className="bg-slate-100 rounded-lg p-1">
        {/* Toggle row */}
        <div className="flex items-center gap-1">
          {(['$', 'POR', '% of Revenue'] as ViewMode[]).map(mode => (
            <button
              key={mode === 'POR' ? '$POR' : mode}
              className={`flex-1 text-xs px-2 py-1.5 rounded-md font-medium transition-colors
                ${viewMode === mode
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'}`}
              onClick={() => handleModeChange(mode)}
            >
              {mode}
            </button>
          ))}
        </div>

        {/* Expandable dept selector — animates open/shut inside the same card */}
        <div
          style={{
            maxHeight: showDepts ? '180px' : '0px',
            opacity: showDepts ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 500ms cubic-bezier(0.4, 0, 0.2, 1), opacity 400ms ease',
          }}
        >
          <div className="pt-3 pb-1 px-1">
            {/* Label fades in and slides up with the container */}
            <div
              style={{
                opacity: showDepts ? 1 : 0,
                transform: showDepts ? 'translateY(0)' : 'translateY(-6px)',
                transition: 'opacity 300ms ease 120ms, transform 300ms ease 120ms',
              }}
              className="text-xs font-medium text-slate-500 mb-2"
            >
              Revenue Departments
            </div>

            {/* Chiclets — revealed one at a time */}
            <div className="flex flex-wrap gap-1.5">
              {availableDepts.map((dept, i) => {
                const isVisible = i < visibleCount;
                const isSelected = selectedDepts.includes(dept);
                return (
                  <button
                    key={dept}
                    onClick={() => toggleDept(dept)}
                    style={{
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.85) translateY(8px)',
                      transition: isVisible
                        ? 'opacity 300ms ease, transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1), border-color 200ms ease, background-color 200ms ease, color 200ms ease'
                        : 'none',
                      pointerEvents: isVisible ? 'auto' : 'none',
                      borderRadius: '20px',
                      border: `1.5px solid ${isSelected ? 'var(--fv3-accent-hover, #E8722A)' : '#E5E7EB'}`,
                      background: isSelected ? 'rgba(232, 114, 42, 0.08)' : 'white',
                      color: isSelected ? 'var(--fv3-accent-hover, #E8722A)' : '#6B7280',
                      fontSize: '12px',
                      fontWeight: 500,
                      padding: '4px 10px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {dept}
                  </button>
                );
              })}
            </div>

            {/* Helper text — fades in after the last chiclet appears */}
            {availableDepts.length > 0 && selectedDepts.length === 0 && (
              <p
                style={{
                  opacity: visibleCount >= availableDepts.length ? 1 : 0,
                  transform: visibleCount >= availableDepts.length ? 'translateY(0)' : 'translateY(-4px)',
                  transition: 'opacity 400ms ease, transform 400ms ease',
                }}
                className="text-xs text-slate-400 mt-2"
              >
                Select departments to calculate % of revenue
              </p>
            )}
          </div>
        </div>
      </div>}

      {/* Mini Chart */}
      {transformedStats && (
        <MiniChart
          data={transformedMonths}
          currentForecast={transformedForecast}
          stats={transformedStats}
          formatValue={formatValue}
          chartKey={chartOfAccountId}
          forwardMonths={transformedForward}
          forwardLabel={forwardLabel}
          currency={currency}
          yFormat={chartYFormat}
        />
      )}

      {/* Stats Grid */}
      {transformedStats && (
        <StatsGrid stats={transformedStats} formatValue={formatValue} />
      )}

      {/* Context Note */}
      {viewMode === '$' ? (
        <ContextNote
          contextNote={analysisData.insight.contextNote}
          statusType={analysisData.insight.statusType}
        />
      ) : transformedInsight ? (
        <ContextNote
          contextNote={transformedInsight.insight.contextNote}
          statusType={transformedInsight.insight.statusType}
        />
      ) : null}
    </div>
  );
}

export default AnalysisTab;
